import NetInfo from '@react-native-community/netinfo';
import { AppState, type AppStateStatus, Platform } from 'react-native';

let latestNetworkStatus = true;
let appState: AppStateStatus = AppState.currentState;
let pendingOfflineTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleInstalled = false;

/** Require sustained offline before flipping — iOS NetInfo flaps on background/resume. */
const OFFLINE_CONFIRM_MS = 2500;

const resolveOnlineState = (state: {
	isConnected?: boolean | null;
	isInternetReachable?: boolean | null;
}): boolean =>
	Boolean(state?.isConnected) && state?.isInternetReachable !== false;

const clearPendingOffline = () => {
	if (pendingOfflineTimer) {
		clearTimeout(pendingOfflineTimer);
		pendingOfflineTimer = null;
	}
};

const commitNetworkStatus = (
	isOnline: boolean,
	callback?: (online: boolean) => void
) => {
	if (latestNetworkStatus === isOnline) return;
	latestNetworkStatus = isOnline;
	callback?.(isOnline);
};

const handleResolvedState = (
	isOnline: boolean,
	callback?: (online: boolean) => void
) => {
	if (isOnline) {
		clearPendingOffline();
		commitNetworkStatus(true, callback);
		return;
	}

	// Background/inactive: NetInfo often reports false offline; ignore until foreground.
	if (Platform.OS !== 'web' && appState !== 'active') {
		return;
	}

	if (pendingOfflineTimer) return;

	pendingOfflineTimer = setTimeout(() => {
		pendingOfflineTimer = null;
		if (Platform.OS !== 'web' && appState !== 'active') return;

		void NetInfo.fetch()
			.then((state) => {
				const stillOffline = !resolveOnlineState(state);
				if (stillOffline) {
					commitNetworkStatus(false, callback);
				}
			})
			.catch(() => {
				/* keep previous status */
			});
	}, OFFLINE_CONFIRM_MS);
};

const ensureLifecycleHooks = (callback?: (online: boolean) => void) => {
	if (lifecycleInstalled || Platform.OS === 'web') return;
	lifecycleInstalled = true;

	AppState.addEventListener('change', (next) => {
		const wasActive = appState === 'active';
		appState = next;

		if (next !== 'active') {
			// Cancel in-flight offline confirm while backgrounded.
			clearPendingOffline();
			return;
		}

		if (!wasActive) {
			// Re-probe on resume; do not trust stale offline from background flaps.
			void refreshNetworkStatus().then((online) => {
				callback?.(online);
			});
		}
	});
};

export const monitorNetworkStatus = (callback: (online: boolean) => void) => {
	ensureLifecycleHooks(callback);

	const unsubscribe = NetInfo.addEventListener((state) => {
		const isOnline = resolveOnlineState(state);
		handleResolvedState(isOnline, callback);
	});

	return unsubscribe;
};

export const getNetworkStatus = (): boolean => latestNetworkStatus;

export const refreshNetworkStatus = async (): Promise<boolean> => {
	try {
		const state = await NetInfo.fetch();
		const isOnline = resolveOnlineState(state);

		// While backgrounded, do not commit a false offline flip into the shared flag.
		if (
			!isOnline &&
			Platform.OS !== 'web' &&
			appState !== 'active'
		) {
			return latestNetworkStatus;
		}

		if (isOnline) {
			clearPendingOffline();
			latestNetworkStatus = true;
			return true;
		}

		// Explicit refresh while active: confirm once more after a short delay path
		// is overkill here — caller asked for current truth.
		latestNetworkStatus = false;
		return false;
	} catch (error) {
		console.warn('Failed to refresh network status:', error);
		return latestNetworkStatus;
	}
};
